// ----------------------------------------------------------------------------
// Component: Accordion
// ----------------------------------------------------------------------------
//
// Generic accordion component.
//
// Requires the following structure:
// .js-accordion__item
//     .js-accordion__button
//     .js-accordion__content
//
// When the button is clicked, 'isOpen' class is added/removed for all three
// elements.
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Styles
import './accordion.scss';

// -------------------------------------
// Variables
// -------------------------------------

var ACCORDION = '.js-accordion';
var ACCORDION_BUTTON = '.js-accordion__button';
var ACCORDION_ITEM = '.js-accordion__item';
var ACCORDION_CONTENT = '.js-accordion__content';
var ACCORDION_TITLE = '.accordion__title';
var ACCORDION_CONTENT_MOBILE = '.js-accordion__content--mobile-only';

// Constant trigger classes
var OPEN_CLASS = 'is-open';

// -------------------------------------
// Helper Functions
// -------------------------------------

function setTabIndex(e, value) {
  e.setAttribute('tabindex', value);
}

// The pixel height an open content panel should sit at: its natural content
// height plus the component's configurable breathing room. 'auto' can't
// express that extra offset, so anything that re-measures an open panel
// (opening it, or re-syncing it on resize) must use this, not 'auto'.
function getOpenHeight(content) {
  var heightOffset = parseFloat(window.getComputedStyle(content).getPropertyValue('--accordion-content-height-offset')) || 0;
  return content.scrollHeight + heightOffset;
}

// Re-measures a panel that JS opened (and so pinned to a pixel height). Panels
// rendered open by Liquid have no inline height and size with CSS 'auto', so
// they're left alone: pinning them early (e.g. on a resize fired before an app
// widget inside has rendered) would freeze them at a too-small height.
function syncOpenHeight(content) {
  if (!content.classList.contains(OPEN_CLASS) || !content.style.height) return;
  content.style.height = getOpenHeight(content) + 'px';
}

// Keeps a pinned open panel in step with content that grows after it opened
// (app widgets, lazy images), which no resize event would report.
var contentResizeObserver = 'ResizeObserver' in window ? new ResizeObserver(function (entries) {
  entries.forEach(function (entry) {
    var content = entry.target.closest(ACCORDION_CONTENT);
    if (content) syncOpenHeight(content);
  });
}) : null;
function observeContent(content) {
  if (!contentResizeObserver) return;
  Array.prototype.forEach.call(content.children, function (child) {
    contentResizeObserver.observe(child);
  });
}

// Runs `callback` once the content's own height transition finishes, replacing
// any handler left over from an interrupted open/close (e.g. rapid toggling)
// so only the most recent action gets to act on the transition end.
function onHeightTransitionEnd(content, callback) {
  if (content._onHeightTransitionEnd) {
    content.removeEventListener('transitionend', content._onHeightTransitionEnd);
  }
  var _handler = function handler(e) {
    if (e.target !== content || e.propertyName !== 'height') return;
    content.removeEventListener('transitionend', _handler);
    content._onHeightTransitionEnd = null;
    callback();
  };
  content._onHeightTransitionEnd = _handler;
  content.addEventListener('transitionend', _handler);
}
function closeAccordion(accordion) {
  var content = accordion.content;
  accordion.button.classList.remove(OPEN_CLASS);
  accordion.item.classList.remove(OPEN_CLASS);
  accordion.button.setAttribute('aria-expanded', 'false');
  content.querySelectorAll('a').forEach(function (el) {
    setTabIndex(el, -1);
  });

  // Lock in the height that's actually on screen right now (not a fresh
  // scrollHeight read, which drops the open height offset below and would
  // itself snap the content by that offset before the collapse even starts).
  content.style.height = content.getBoundingClientRect().height + 'px';
  content.offsetHeight; // Force reflow so the start height is registered.

  content.classList.remove(OPEN_CLASS);
  content.style.height = '0px';
  onHeightTransitionEnd(content, function () {
    content.hidden = true;
    content.style.height = null;
  });
}
function openAccordion(accordion) {
  var content = accordion.content;
  accordion.button.classList.add(OPEN_CLASS);
  accordion.item.classList.add(OPEN_CLASS);
  accordion.button.setAttribute('aria-expanded', 'true');
  content.querySelectorAll('a').forEach(function (el) {
    setTabIndex(el, 0);
  });
  content.hidden = false;
  content.style.height = '0px';
  content.offsetHeight; // Force reflow so the transition starts from 0.

  content.classList.add(OPEN_CLASS);
  // Stays pinned to this explicit pixel value (not 'auto') once open: the
  // height offset baked into it has no equivalent in 'auto', so switching
  // to 'auto' after the transition would drop it and snap the content up.
  content.style.height = getOpenHeight(content) + 'px';
}
function toggleAccordion(accordion) {
  if (accordion.isOpening && accordion.container.hasAttribute('data-collapse-all')) {
    accordion.container.querySelectorAll(ACCORDION_ITEM).forEach(function (item) {
      var button = item.querySelector(ACCORDION_BUTTON);
      var content = item.querySelector(ACCORDION_CONTENT);

      // Only items actually open right now: closing an already-closed item
      // (e.g. the one about to be opened) is a 0-to-0 height no-op that
      // never fires 'transitionend', leaving its close callback dangling
      // until the real open transition ends and triggers it by mistake.
      if (button && content && button.classList.contains(OPEN_CLASS)) {
        closeAccordion({
          button,
          item,
          content
        });
      }
    });
  }
  if (accordion.isOpening) {
    openAccordion(accordion);
  } else {
    closeAccordion(accordion);
  }
}
function accordionEventHandle(e) {
  var button = e.target.closest(ACCORDION_BUTTON);
  var item = button.closest(ACCORDION_ITEM);
  var title = button.closest(ACCORDION_TITLE);
  var container = button.closest(ACCORDION);

  // Store elements as an object
  var accordion = {
    button: button,
    item: item,
    title: title,
    content: title.nextElementSibling,
    isOpening: !button.classList.contains(OPEN_CLASS),
    container: container,
    button_group: container.querySelectorAll(ACCORDION_BUTTON)
  };

  // Collapse any open sibling & children accordions if data attribute set to true
  if (e.type === 'click') {
    e.preventDefault();
    toggleAccordion(accordion);
  } else if (e.type === 'keydown') {
    var key = e.which.toString();
    if (key.match(/35|36|38|40/)) {
      e.preventDefault();
      var index = Array.prototype.indexOf.call(accordion.button_group, button);
      var length = accordion.button_group.length;
      var newIndex;
      switch (key) {
        case '38':
          // Up
          newIndex = (index + length + -1) % length;
          accordion.button_group[newIndex].focus();
          break;
        case '40':
          // Down
          newIndex = (index + length + 1) % length;
          accordion.button_group[newIndex].focus();
          break;
        case '35':
          // End
          accordion.button_group[length - 1].focus();
          break;
        case '36':
          // Home
          accordion.button_group[0].focus();
          break;
      }
    }
  }
}
function syncAccordionLinks(content) {
  var value = content.classList.contains(OPEN_CLASS) ? 0 : -1;
  content.querySelectorAll('a').forEach(function (e) {
    setTabIndex(e, value);
  });
}
function openAccordionFromHash() {
  var hash = window.location.hash;
  if (hash.length <= 1) {
    return;
  }
  var targetId;
  try {
    targetId = decodeURIComponent(hash.slice(1));
  } catch (_unused) {
    return;
  }
  var target = document.getElementById(targetId);
  if (!target || !target.matches(ACCORDION_ITEM)) {
    return;
  }
  var button = target.querySelector(ACCORDION_BUTTON);
  var content = target.querySelector(ACCORDION_CONTENT);
  var container = target.closest(ACCORDION);
  if (!button || !content || !container) {
    return;
  }
  toggleAccordion({
    button: button,
    item: target,
    content: content,
    container: container,
    isOpening: true
  });
}

// -------------------------------------
// Hooks
// -------------------------------------

document.addEventListener('DOMContentLoaded', function () {
  var accordionButtons = document.querySelectorAll(ACCORDION_BUTTON);
  document.querySelectorAll(ACCORDION_CONTENT).forEach(function (e) {
    syncAccordionLinks(e);
    observeContent(e);
  });
  accordionButtons.forEach(function (e) {
    e.addEventListener('click', accordionEventHandle);
    e.addEventListener('keydown', accordionEventHandle);
  });
  openAccordionFromHash();
});
window.addEventListener('hashchange', openAccordionFromHash);
window.addEventListener('resize', function () {
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 1024) {
    // On mobile, find all accordion content links of the close accordions to add tabindex -1
    var accordionContentLinks = document.querySelectorAll("".concat(ACCORDION_CONTENT, ":not(.").concat(OPEN_CLASS, ") a"));
    accordionContentLinks.forEach(function (e) {
      setTabIndex(e, -1);
    });
  } else {
    // If on desktop, find all accordion content links that belongs to the #accordion-cart--content on the cart page to remove tabindex -1.
    var accordionCartContentLinks = document.querySelectorAll("".concat(ACCORDION_CONTENT).concat(ACCORDION_CONTENT_MOBILE, " a"));
    accordionCartContentLinks.forEach(function (e) {
      setTabIndex(e, 0);
    });
  }

  // Re-adjust all the expanded accordion content tab height on "resize" event.
  // Not 'auto': that drops the height offset baked into the open height and
  // snaps the content by that amount (also fires on any viewport-affecting
  // event, e.g. opening devtools).
  var expandedAccordionContent = document.querySelectorAll("".concat(ACCORDION_CONTENT, ".").concat(OPEN_CLASS));
  expandedAccordionContent.forEach(syncOpenHeight);
});